import { Menu, Transition } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { Spinner } from "@via/components/loading/loading.tsx";
import { classNames } from "@via/utils/helpers.tsx";
import { type TableContextMenuInterface } from "@via/utils/interfaces.tsx";
import { type JSX, type ReactNode, Fragment } from "react";

export default function ContextButton({
  id,
  options,
  onSelect,
  text,
  className,
  loading = false,
  disabled = false,
  onMouseEnter,
  onMouseLeave,
}: {
  id?: string;
  options: TableContextMenuInterface[];
  onSelect: (option: TableContextMenuInterface) => void;
  text: ReactNode;
  className?: string;
  loading?: boolean;
  disabled?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}): JSX.Element {
  return (
    <Menu
      id={id}
      as="div"
      className="relative inline-block text-left"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}>
      <div>
        <Menu.Button
          className={classNames(
            "inline-flex justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
            className ??
              "bg-indigo-600 hover:bg-indigo-500 focus-visible:outline-indigo-600"
          )}
          disabled={disabled || loading}>
          <Spinner loading={loading}>
            {text}{" "}
            {options.length > 0 && (
              <ChevronDownIcon
                className="-mr-1.5 ml-0.5 h-4 w-4"
                aria-hidden="true"
              />
            )}
          </Spinner>
        </Menu.Button>
      </div>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95">
        <Menu.Items className="absolute right-0 z-11 mt-2 w-auto min-w-40 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
          <div className="py-1">
            {options.map(item => (
              <Menu.Item key={item.name} disabled={item.disabled}>
                {({ active, close, disabled }) => (
                  <button
                    onClick={e => {
                      e.preventDefault();
                      if (disabled) return;
                      onSelect(item);
                      close();
                    }}
                    disabled={disabled}
                    className={classNames(
                      disabled
                        ? "text-gray-400 cursor-not-allowed"
                        : "text-gray-900",
                      active && !disabled ? "bg-gray-50" : "",
                      "block w-full px-3 py-1 text-left text-sm leading-6"
                    )}>
                    <span className="block truncate" title={item.name}>
                      {item.name}
                    </span>
                  </button>
                )}
              </Menu.Item>
            ))}
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}
